import { useId, useRef, useState } from 'react'
import { ExternalLink, Radio, Trash2, UserMinus, UserPlus } from 'lucide-react'
import {
  Avatar,
  Badge,
  Button,
  ConfirmDialog,
  CopyField,
  IconButton,
  IconButtonLink,
  Notice,
  Skeleton,
  SkeletonGroup,
  copyTextFrom,
  toast,
} from '@/components/play'
import { WorkspaceInfo } from '@/components/workspace/WorkspaceInfo'
import { useEventHosts } from '@/components/workspace/useEventHosts'
import { useWorkspaceExit, type WorkspaceExit } from '@/components/workspace/useWorkspaceExit'
import { publicEventPath } from '@/lib/eventLinks'
import { expiresIn, hostsError, madeAgo } from '@/lib/hosts/api'
import type { Host, HostInvitation } from '@/lib/hosts/types'
import { useAuth } from '@/state/AuthContext'
import type { EventResource } from '@/types/events'

const newestFirst = (a: HostInvitation, b: HostInvitation) => Date.parse(b.created_at) - Date.parse(a.created_at)

/**
 * The event's hosts on the workspace's Overview, beside the Live show (docs/features/play-event-workspace.md): the
 * owner organization's stage first, then each person host with whether they're on stage, a link to their stage and
 * Remove; pending invitations with Copy and Revoke; and Invite a host, which makes a single-use link and copies it.
 * The event's managers invite and remove hosts as its organizers do. Renders nothing where the hosts endpoint answers
 * 404.
 *
 * The list it follows also carries the team (the Team view, `EventTeam`), so it notices the team changing here too: a
 * manager whom an organizer removes (the pushed list no longer has them) goes to their events through `exit`, and the
 * organization's side hears "<name> joined as a manager".
 *
 * On stage means a seat is taken, not that video arrives (production LiveKit sends no webhooks; seats reconcile within
 * about three minutes), so it is never shown in the live green.
 */
export function EventHosts({ event, exit: workspaceExit }: { event: EventResource; exit?: WorkspaceExit }) {
  const { organization, user } = useAuth()
  const ownExit = useWorkspaceExit()
  const exit = workspaceExit ?? ownExit
  const managesTeam = event.attributes.can_manage_managers ?? event.attributes.access !== 'manager'
  const { hosts, invitations, owner, loaded, loading, error, unavailable, load, invite, revoke, removeHost } =
    useEventHosts(event.id, {
      selfId: managesTeam ? null : user?.id,
      onSelfRemoved: () => exit.lost(event.attributes.name),
      onManagerJoined: (manager) => {
        if (managesTeam) toast({ title: `${manager.name} joined as a manager`, tone: 'success' })
      },
    })
  const headingId = useId()
  const invitesId = useId()
  const inviteButton = useRef<HTMLButtonElement>(null)
  // Set once a confirmed removal or revocation succeeds, so focus moves to Invite a host instead of the row that went.
  const done = useRef(false)
  const [inviting, setInviting] = useState(false)
  const [inviteError, setInviteError] = useState('')
  const [removal, setRemoval] = useState<Host | null>(null)
  const [removalOpen, setRemovalOpen] = useState(false)
  const [removalError, setRemovalError] = useState('')
  const [revocation, setRevocation] = useState<HostInvitation | null>(null)
  const [revocationOpen, setRevocationOpen] = useState(false)
  const [revocationError, setRevocationError] = useState('')

  if (unavailable) return null

  const yours = !!organization && event.attributes.organization_id === organization.id
  // The host as the latest list has them, so a refresh while the dialog is open updates its warning.
  const removing = removal && (hosts.find((host) => host.stage_id === removal.stage_id) ?? removal)
  const finalFocus = () => (done.current ? inviteButton.current : true)

  async function createInvitation() {
    if (inviting) return
    setInviting(true)
    setInviteError('')
    // Both start inside the click, so the clipboard keeps the click's permission while the link is made.
    const request = invite()
    const copied = copyTextFrom(request.then((invitation) => invitation.url))
    try {
      const invitation = await request
      const expiry = expiresIn(invitation.expires_at)
      if (await copied) {
        toast({ title: 'Invite link copied', description: expiry ? `Works once. ${expiry}.` : 'Works once.', tone: 'success' })
      } else {
        toast({ title: 'Invite link ready', description: 'Copying didn’t work. Copy it from Pending invites.' })
      }
    } catch (failure) {
      setInviteError(hostsError(failure, 'An invite link couldn’t be created. Try again.'))
    } finally {
      setInviting(false)
    }
  }

  function askRemoval(host: Host) {
    done.current = false
    setRemoval(host)
    setRemovalError('')
    setRemovalOpen(true)
  }

  async function confirmRemoval() {
    if (!removing) return
    const { name } = removing.profile
    try {
      await removeHost(removing.stage_id)
    } catch (failure) {
      setRemovalError(hostsError(failure, `${name} couldn’t be removed. Try again.`))
      throw failure
    }
    done.current = true
    toast({ title: `Removed ${name}` })
  }

  function askRevocation(invitation: HostInvitation) {
    done.current = false
    setRevocation(invitation)
    setRevocationError('')
    setRevocationOpen(true)
  }

  async function confirmRevocation() {
    if (!revocation) return
    try {
      await revoke(revocation.id)
    } catch (failure) {
      setRevocationError(hostsError(failure, 'The invite link couldn’t be revoked. Try again.'))
      throw failure
    }
    done.current = true
    toast({ title: 'Invite link revoked' })
  }

  return (
    <section className="pw-hosts" aria-labelledby={headingId}>
      <div className="pw-hosts-heading">
        <h2 id={headingId}>Hosts</h2>
        <WorkspaceInfo label="About hosts">
          Each host gets their own stage and chat on your event page. An invite link works once, for 14 days.
        </WorkspaceInfo>
      </div>
      {!loaded ? (
        loading || !error ? (
          <SkeletonGroup label="Loading hosts" className="pw-hosts-list">
            {[0, 1].map((row) => (
              <div key={row} className="pw-host">
                <Skeleton shape="circle" className="pw-host-skeleton-avatar" />
                <Skeleton className="pw-host-skeleton-text" />
              </div>
            ))}
          </SkeletonGroup>
        ) : (
          <Notice
            tone="error"
            action={<Button variant="secondary" size="compact" onClick={() => void load()}>Try again</Button>}
          >
            {error}
          </Notice>
        )
      ) : (
        <>
          {/* role="list": Safari drops list semantics from lists styled without markers. */}
          <ul className="pw-hosts-list" role="list" aria-labelledby={headingId}>
            {owner ? (
              <StageRow
                name={owner.profile.name}
                handle={owner.profile.handle}
                avatarUrl={owner.profile.avatar_url}
                onStage={owner.live}
                tag={yours ? 'Your stage' : undefined}
                href={owner.path}
                linkLabel={`Open ${yours ? 'your' : `${owner.profile.name}’s`} stage`}
              />
            ) : (
              // An API without the owner: the signed-in organization, linking the event's public page.
              <StageRow
                name={organization && yours ? organization.attributes.name : 'Event owner'}
                tag={yours ? 'Your stage' : undefined}
                href={publicEventPath(event)}
                linkLabel="Open event page"
              />
            )}
            {hosts.map((host) => (
              <StageRow
                key={host.stage_id}
                name={host.profile.name}
                handle={host.profile.handle}
                avatarUrl={host.profile.avatar_url}
                onStage={host.live}
                href={host.path}
                linkLabel={`Open ${host.profile.name}’s stage`}
                onRemove={() => askRemoval(host)}
              />
            ))}
          </ul>
          {!hosts.length && !invitations.length && (
            <p className="pw-hosts-empty">Hosts you invite get their own stage and chat.</p>
          )}
          {invitations.length > 0 && (
            <div className="pw-host-invites">
              <h3 id={invitesId}>Pending invites</h3>
              <ul aria-labelledby={invitesId} role="list">
                {[...invitations].sort(newestFirst).map((invitation) => (
                  <li key={invitation.id} className="pw-host-invite">
                    <div className="pw-host-invite-row">
                      <CopyField
                        label="Invite link"
                        hideLabel
                        size="compact"
                        value={invitation.url}
                        copyLabel="Copy invite link"
                      />
                      <IconButton
                        label="Revoke invite link"
                        variant="ghost"
                        size="compact"
                        onClick={() => askRevocation(invitation)}
                      >
                        <Trash2 />
                      </IconButton>
                    </div>
                    <span className="pw-host-invite-timing">
                      {[madeAgo(invitation.created_at), expiresIn(invitation.expires_at)].filter(Boolean).join(' · ')}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <Button ref={inviteButton} variant="secondary" loading={inviting} onClick={() => void createInvitation()}>
            <UserPlus />
            Invite a host
          </Button>
          {inviteError && <Notice tone="error" alert>{inviteError}</Notice>}
        </>
      )}
      <ConfirmDialog
        open={removalOpen}
        onOpenChange={setRemovalOpen}
        title={`Remove ${removing?.profile.name ?? 'host'}?`}
        description={`${removing?.live ? 'They’re on stage now and will be taken off. ' : ''}Their stage and its chat will be deleted.`}
        confirmLabel="Remove host"
        onConfirm={confirmRemoval}
        failureMessage={removalError || undefined}
        finalFocus={finalFocus}
      />
      <ConfirmDialog
        open={revocationOpen}
        onOpenChange={setRevocationOpen}
        title="Revoke this invite link?"
        description="It stops working right away."
        confirmLabel="Revoke link"
        onConfirm={confirmRevocation}
        failureMessage={revocationError || undefined}
        finalFocus={finalFocus}
      />
    </section>
  )
}

/** One stage: avatar, name, whether a seat is taken, @handle, and its link; Remove for a person host. The actions keep
 *  two slots, so the links line up whether or not a row can be removed. */
function StageRow({
  name,
  handle,
  avatarUrl,
  onStage,
  tag,
  href,
  linkLabel,
  onRemove,
}: {
  name: string
  handle?: string
  avatarUrl?: string | null
  /** Left out when unknown, as for the fallback owner. */
  onStage?: boolean
  tag?: string
  href: string
  linkLabel: string
  onRemove?: () => void
}) {
  return (
    <li className="pw-host">
      <Avatar name={name} src={avatarUrl} />
      <div className="pw-host-text">
        <span className="pw-host-name">{name}</span>
        <span className="pw-host-meta">
          {onStage === true && <Badge tone="neutral" icon={<Radio />}>On stage</Badge>}
          {onStage === false && <Badge tone="quiet">Not on stage</Badge>}
          {handle && <span>@{handle}</span>}
          {tag && <span>{tag}</span>}
        </span>
      </div>
      <div className="pw-host-actions">
        <IconButtonLink to={href} target="_blank" rel="noreferrer" variant="ghost" label={linkLabel}>
          <ExternalLink />
        </IconButtonLink>
        {onRemove && (
          <IconButton label={`Remove ${name}`} variant="ghost" onClick={onRemove}>
            <UserMinus />
          </IconButton>
        )}
      </div>
    </li>
  )
}
